// Extracted from ProductsPage.js. Generic "Add to
// Quotation" dialog for thickness/weight variant products, incl. the
// adhesive primary/secondary rate chip selector.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';

export function VariantQuickAddDialog({
  selectedGroup,
  selectedThickness,
  onClose,
  getProductForThickness,
  getSizeOptions,
  getCalculatedRate,
  selectedSize,
  setSelectedSize,
  quantityInput,
  setQuantityInput,
  setQuantity,
  adhesiveRateChoice,
  setAdhesiveRateChoice,
  onAdd,
}) {
  return (
    <Dialog open={!!selectedGroup && !!selectedThickness} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-heda-ink">Add to Quotation</DialogTitle>
        </DialogHeader>
        {selectedGroup && selectedThickness && (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="p-3 bg-heda-cream/60 rounded-xl">
              <p className="font-bold text-heda-ink">{selectedGroup.brand}</p>
              <p className="text-sm text-heda-sub">{selectedGroup.name}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedThickness && selectedThickness !== 'default' && (
                  <Badge className="rounded-full bg-heda-ink text-white">{selectedThickness}</Badge>
                )}
                {getProductForThickness(selectedGroup, selectedThickness)?.size && (
                  <Badge variant="outline" className="rounded-full border-heda-gold text-heda-gold">
                    {getProductForThickness(selectedGroup, selectedThickness)?.size}
                  </Badge>
                )}
                {getProductForThickness(selectedGroup, selectedThickness)?.grade && (
                  <Badge className="rounded-full" variant="outline">{getProductForThickness(selectedGroup, selectedThickness)?.grade}</Badge>
                )}
              </div>
              <p className="mt-2 font-mono-data font-bold text-heda-olive-dark">
                {formatINR(getProductForThickness(selectedGroup, selectedThickness)?.base_rate || 0)}
                <span className="text-xs font-normal text-heda-mute ml-1">
                  /{selectedGroup.rate_type?.replace('per ', '')}
                </span>
              </p>
            </div>

            {/* Size Selection for per sq ft products */}
            {selectedGroup.rate_type === 'per sq ft' && (
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-2 block">
                  Select Size {selectedGroup.isDoor && <span className="text-heda-mute">(in inches)</span>}
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {(() => {
                    const product = getProductForThickness(selectedGroup, selectedThickness);
                    const sizeOptions = getSizeOptions(selectedGroup);
                    const defaultSizes = sizeOptions.map(s => s.value);
                    const availableSizes = product?.available_sizes?.length > 0 ? product.available_sizes : defaultSizes;
                    // Only show sizes that are available
                    const visibleSizes = sizeOptions.filter(size => availableSizes.includes(size.value));
                    return visibleSizes.map(size => (
                      <button
                        key={size.value}
                        onClick={() => setSelectedSize(size.value)}
                        className={`p-2 text-sm rounded-xl border transition-all ${
                          selectedSize === size.value
                            ? 'bg-heda-ink text-white border-heda-ink'
                            : 'bg-white text-heda-ink border-heda-line hover:border-heda-gold'
                        }`}
                      >
                        <span className="font-semibold">{size.label}</span>
                        <span className="text-xs block opacity-70">{size.sqft.toFixed(2)} sq.ft</span>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Quantity</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newQty = Math.max(1, (parseInt(quantityInput) || 1) - 1);
                    setQuantity(newQty);
                    setQuantityInput(String(newQty));
                  }}
                  className="rounded-xl h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                >-</Button>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={quantityInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setQuantityInput(val);
                    if (val) {
                      setQuantity(parseInt(val) || 1);
                    }
                  }}
                  onBlur={() => {
                    // Ensure minimum 1 on blur
                    if (!quantityInput || parseInt(quantityInput) < 1) {
                      setQuantityInput('1');
                      setQuantity(1);
                    }
                  }}
                  className="rounded-xl bg-heda-cream/50 text-center h-10 w-20 font-bold text-lg"
                  data-testid="quantity-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newQty = (parseInt(quantityInput) || 1) + 1;
                    setQuantity(newQty);
                    setQuantityInput(String(newQty));
                  }}
                  className="rounded-xl h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                >+</Button>
              </div>
            </div>

            {/* Adhesive rate-type chip selector — only shown when the
                resolved product has both a primary and a secondary rate
                (e.g. ARGO TAPE at ₹240/piece and ₹1200/bundle). */}
            {(() => {
              const p = getProductForThickness(selectedGroup, selectedThickness);
              const secondaryRate = parseFloat(p?.secondary_base_rate) || 0;
              if (!p || p.main_category !== 'Adhesives' || secondaryRate <= 0) return null;
              return (
                <div>
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Rate Type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setAdhesiveRateChoice('primary')}
                      className={`px-3 py-2 rounded-xl border text-center transition-all ${
                        adhesiveRateChoice === 'primary'
                          ? 'border-heda-ink bg-heda-ink text-white'
                          : 'border-heda-line bg-white hover:border-heda-olive/50'
                      }`}
                      data-testid="adhesive-rate-primary"
                    >
                      <p className={`text-xs font-mono-data ${adhesiveRateChoice === 'primary' ? 'text-white/80' : 'text-heda-mute'}`}>{p.rate_type}</p>
                      <p className="text-sm font-bold">{formatINR(p.base_rate)}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdhesiveRateChoice('secondary')}
                      className={`px-3 py-2 rounded-xl border text-center transition-all ${
                        adhesiveRateChoice === 'secondary'
                          ? 'border-heda-ink bg-heda-ink text-white'
                          : 'border-heda-line bg-white hover:border-heda-olive/50'
                      }`}
                      data-testid="adhesive-rate-secondary"
                    >
                      <p className={`text-xs font-mono-data ${adhesiveRateChoice === 'secondary' ? 'text-white/80' : 'text-heda-mute'}`}>{p.secondary_rate_type || 'per bundle'}</p>
                      <p className="text-sm font-bold">{formatINR(secondaryRate)}</p>
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Calculation */}
            <div className="p-3 bg-heda-gold-tint rounded-xl border border-heda-gold/30">
              <p className="text-xs text-heda-gold uppercase tracking-wide mb-1">Total Calculation</p>
              {selectedGroup.rate_type === 'per sq ft' && (
                <p className="text-sm text-heda-sub">
                  {formatINR(getProductForThickness(selectedGroup, selectedThickness)?.base_rate || 0)}/sq.ft × {(getSizeOptions(selectedGroup).find(s => s.value === selectedSize)?.sqft || 32).toFixed(2)} sq.ft × {parseInt(quantityInput) || 1} nos
                </p>
              )}
              {selectedGroup.rate_type !== 'per sq ft' && (
                <p className="text-sm text-heda-sub">
                  {formatINR(getProductForThickness(selectedGroup, selectedThickness)?.base_rate || 0)} × {parseInt(quantityInput) || 1} nos
                </p>
              )}
              <p className="text-xl font-bold font-mono-data text-heda-ink mt-1">
                {formatINR(getCalculatedRate(getProductForThickness(selectedGroup, selectedThickness), selectedGroup) * (parseInt(quantityInput) || 1))}
              </p>
            </div>

            {/* Add Button */}
            <Button
              onClick={onAdd}
              className="rounded-xl w-full bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-12"
              data-testid="confirm-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add to Quotation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
